/**
 * Adaptador Order/OrderLine/Payment (filas PowerSync) → `ReceiptData`.
 *
 * Keeps `receipt.ts` DB-agnostic: filters voided lines
 * (soft-delete), nullables are normalized and visible names resolved.
 */

import type { Order, OrderLine, Payment } from "@comandero/powersync";
import type { ReceiptData, ReceiptLabels } from "./receipt";

export interface ReceiptContext {
  restaurantName: string;
  branchName?: string | null;
  tableName?: string | null;
  employeeName?: string | null;
  footer?: string | null;
  labels?: Partial<ReceiptLabels>;
}

/** Builds ticket `ReceiptData` from order rows. */
export function orderToReceiptData(
  order: Order,
  lines: OrderLine[],
  payments: Payment[],
  ctx: ReceiptContext,
): ReceiptData {
  const liveLines = lines.filter((l) => !l.deleted_at);
  return {
    restaurantName: ctx.restaurantName,
    branchName: ctx.branchName ?? null,
    orderId: order.id,
    timestamp: order.closed_at ?? order.opened_at ?? order.created_at ?? null,
    tableName: ctx.tableName ?? null,
    employeeName: ctx.employeeName ?? null,
    items: liveLines.map((l) => ({
      name: l.name ?? "",
      qty: l.qty ?? 1,
      unitPrice: l.unit_price ?? 0,
      lineTotal: l.line_total ?? 0,
    })),
    subtotal: order.subtotal ?? 0,
    discount: order.discount ?? 0,
    taxTotal: order.tax_total ?? 0,
    total: order.total ?? 0,
    payments: payments
      .filter((p) => !p.deleted_at)
      .map((p) => ({
        method: p.method ?? "cash",
        amount: p.amount ?? 0,
        tip: p.tip ?? 0,
        tendered: p.tendered ?? undefined,
        changeGiven: p.change_given ?? 0,
      })),
    footer: ctx.footer ?? null,
    labels: ctx.labels,
  };
}
